import * as React from "react";
import { Link } from "@tanstack/react-router";
import { ArrowRight, type LucideIcon } from "lucide-react";

import { cn } from "@/lib/utils";

export interface PathCardProps {
  to: string;
  icon: LucideIcon;
  eyebrow?: string;
  title: string;
  body: string;
  items?: readonly string[];
  cta: string;
  className?: string;
  accent?: "brand" | "violet";
}

/**
 * Shared card used by the "Three paths" and "Services" homepage sections.
 * The entire card is a single anchor — no nested interactive elements.
 */
export function PathCard({
  to,
  icon: Icon,
  eyebrow,
  title,
  body,
  items,
  cta,
  className,
  accent = "brand",
}: PathCardProps) {
  return (
    <Link
      to={to}
      className={cn(
        "group relative flex h-full flex-col overflow-hidden rounded-xl border border-ink-200 bg-paper-0 p-7 shadow-[0_1px_2px_oklch(0.1831_0.0309_263.38_/_0.04),0_12px_32px_-18px_oklch(0.1831_0.0309_263.38_/_0.16)] transition-brand hover:border-brand-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-700 focus-visible:ring-offset-2 focus-visible:ring-offset-background",
        className,
      )}
    >
      <span
        className={cn(
          "absolute inset-x-0 top-0 h-1",
          accent === "violet"
            ? "bg-[linear-gradient(90deg,var(--color-accent-600),var(--color-accent-400))]"
            : "bg-[linear-gradient(90deg,var(--color-brand-600),var(--color-brand-400))]",
        )}
        aria-hidden="true"
      />
      <span
        className="flex size-10 items-center justify-center rounded-md bg-brand-50"
        aria-hidden="true"
      >
        <Icon size={20} strokeWidth={1.5} className="text-brand-700" />
      </span>

      {eyebrow ? (
        <span
          className={cn(
            "mt-5 font-mono text-stat",
            accent === "violet" ? "text-accent-500" : "text-brand-600",
          )}
        >
          {eyebrow}
        </span>
      ) : null}

      <h3 className={cn("font-display text-h3 text-ink-900", eyebrow ? "mt-2" : "mt-5")}>
        {title}
      </h3>

      <p className="mt-3 text-body-sm text-ink-600">{body}</p>

      {items?.length ? (
        <>
          <span className="mt-6 block h-px w-full bg-ink-200" aria-hidden="true" />
          <ul className="mt-5 space-y-2">
            {items.map((item) => (
              <li key={item} className="flex items-start gap-2.5 text-body-sm text-ink-600">
                <span
                  className="mt-2 size-1.5 shrink-0 rounded-full bg-brand-600"
                  aria-hidden="true"
                />
                {item}
              </li>
            ))}
          </ul>
        </>
      ) : null}

      <span
        className={cn(
          "mt-auto inline-flex items-center gap-2 pt-6 text-body-sm font-medium",
          accent === "violet" ? "text-accent-600" : "text-brand-700",
        )}
      >
        {cta}
        <ArrowRight
          size={20}
          strokeWidth={1.5}
          className="transition-transform duration-150 group-hover:translate-x-1"
          aria-hidden="true"
        />
      </span>
    </Link>
  );
}

export default PathCard;
